/**
 * OrganizationOnboarding Component
 * A multi-step wizard (4 steps) that guides users through registering a new
 * organization in the Colorado STEM Ecosystem. This is the self-service
 * onboarding flow that replaces the legacy manual Google Form workflow.
 *
 * Key behaviors:
 * - Requires authentication; redirects unauthenticated users to /login
 * - Step 1: Basic Info (name, sector, description, logo upload)
 * - Step 2: Contact & Location (email, website, regions served)
 * - Step 3: Programs & Partnerships (Programming types, offerings, needs)
 * - Step 4: Review & Submit (read-only summary with clickable sections to edit)
 * - Per-step validation runs before advancing; step 3 has no required fields
 * - On submit, uploads logo to S3 first (if provided), then creates the org
 * - After success, redirects to dashboard after a brief delay
 * - Organization is created with 'pending' status, requiring admin approval
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Card,
    ProgressBar,
    Badge,
    Spinner,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import { useOrganizationApi } from '@/shared/services/organizationApi';
import {
    normalizeUrl,
    hasUnsupportedScheme,
} from '@/shared/utils/normalizeUrl';
import { US_TIMEZONES } from '@/shared/constants/timezones';
import './Organizations.css';

// Wizard step definitions used to render the step indicator bar.
// Each step has a number, display title, and icon (shows a checkmark
// when completed, the step number when current/future).
const STEPS = [
    { num: 1, title: 'Basic Info', icon: '1' },
    { num: 2, title: 'Contact & Location', icon: '2' },
    { num: 3, title: 'Programs', icon: '3' },
    { num: 4, title: 'Review', icon: '4' },
];

export default function OrganizationOnboarding() {
    const { user, loading: authLoading } = useAuth();
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name || 'this ecosystem';

    // ---- Wizard State ----
    const [step, setStep] = useState(1); // Current wizard step (1-4)
    const [formData, setFormData] = useState({
        name: '',
        primarySector: '',
        secondarySectors: [],
        description: '',
        email: '',
        website: '',
        imageUrl: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        timezone: '',
        regionsServed: [],
        programmingTypes: [],
        offerings: '', // Newline-delimited text; split into array on submit
        needs: '', // Newline-delimited text; split into array on submit
    });

    // ---- Enum Options (fetched from API) ----
    // These populate the select/checkbox options and are shared across
    // the onboarding form and the edit form (OrganizationEdit).
    const [sectors, setSectors] = useState([]);
    const [regions, setRegions] = useState([]);
    const [taxonomyProgrammingTypes, setTaxonomyProgrammingTypes] = useState(
        []
    );

    // ---- UI State ----
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    // Hidden file input ref -- the visible button programmatically clicks this
    const fileInputRef = useRef(null);
    // Name uniqueness check state
    const [nameExists, setNameExists] = useState(false);
    const [nameCheckLoading, setNameCheckLoading] = useState(false);
    const nameCheckTimeout = useRef(null);

    const orgApi = useOrganizationApi();
    const navigate = useNavigate();

    // Auth guard: redirect unauthenticated users to login page.
    // LogIn reads `?redirect=` from the query string (the `state: { from }`
    // shape from before was dead code: LogIn never read it).
    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/login?redirect=/organizations/onboard');
        }
    }, [user, authLoading, navigate]);

    // Load enum values (sectors, regions, programming types) from the API.
    // These are static reference data used to populate form dropdowns and
    // checkbox groups across all steps.
    useEffect(() => {
        const loadEnums = async () => {
            const [s, r, p] = await Promise.all([
                orgApi.getSectors(),
                orgApi.getRegions(),
                orgApi.getProgrammingTypes(),
            ]);
            setSectors(s);
            setRegions(r);
            setTaxonomyProgrammingTypes(p);
        };
        loadEnums();
    }, [orgApi]);

    // Generic handler for text/select inputs. Updates the corresponding
    // field in formData and clears any validation error for that field
    // so the error message disappears as the user corrects it.
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (validationErrors[name]) {
            setValidationErrors((prev) => ({ ...prev, [name]: null }));
        }
        // Reset name-exists warning when the user changes the name
        if (name === 'name') {
            setNameExists(false);
        }
    };

    // Debounced name uniqueness check: fires when the user stops
    // typing in the name field. Calls the lightweight check-name
    // endpoint and sets nameExists to show an inline warning.
    const checkNameAvailability = useCallback(
        (value) => {
            clearTimeout(nameCheckTimeout.current);
            const trimmed = value?.trim();
            if (!trimmed || trimmed.length < 3) {
                setNameExists(false);
                return;
            }
            nameCheckTimeout.current = setTimeout(async () => {
                try {
                    setNameCheckLoading(true);
                    const result = await orgApi.checkName(trimmed);
                    setNameExists(result.exists);
                } catch {
                    // Silently ignore: the submit-time check is the fallback
                } finally {
                    setNameCheckLoading(false);
                }
            }, 500);
        },
        [orgApi]
    );

    // Toggle handler for checkbox groups (secondarySectors, regionsServed,
    // programmingTypes). Adds the value if not present, removes it if already
    // selected. Also clears any validation error for the field.
    const handleMultiSelect = (name, value) => {
        setFormData((prev) => {
            const current = prev[name] || [];
            if (current.includes(value)) {
                return { ...prev, [name]: current.filter((v) => v !== value) };
            }
            return { ...prev, [name]: [...current, value] };
        });
        if (validationErrors[name]) {
            setValidationErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    // Handles logo file selection from the hidden file input.
    // Validates file type and size (max 5MB), creates a data URL preview
    // via FileReader, and stores the File object in formData for later
    // upload to S3 during form submission.
    const handleImageChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image must be less than 5MB');
            return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        // For now, store the file for later upload
        // In production, this would upload to S3 and get back a URL
        setFormData((prev) => ({ ...prev, imageFile: file }));
    }, []);

    // Per-step validation. Called when the user clicks "Next" to advance.
    // Returns true if validation passes, false otherwise.
    // Step 1: name, primarySector, description are required
    // Step 2: regionsServed is required; email is optional but validated for
    // format. website is optional too -- a bare domain like
    // "historycolorado.org" is auto-corrected to https:// on submit rather
    // than rejected here; only an explicit non-http(s) scheme (e.g.
    // "ftp://") is flagged.
    // Step 3: no required fields (all optional)
    // Step 4: review-only, no validation needed
    const validateStep = (stepNum) => {
        const errors = {};

        if (stepNum === 1) {
            if (!formData.name?.trim()) {
                errors.name = 'Organization name is required';
            } else if (nameExists) {
                errors.name =
                    'An organization with this name already exists. You can claim it from the directory instead.';
            }
            if (!formData.primarySector) {
                errors.primarySector = 'Primary sector is required';
            }
            if (!formData.description?.trim()) {
                errors.description = 'Description is required';
            } else if (formData.description.length > 2000) {
                errors.description =
                    'Description must be less than 2000 characters';
            }
        }

        if (stepNum === 2) {
            if (formData.regionsServed.length === 0) {
                errors.regionsServed = 'Please select at least one region';
            }
            if (
                formData.email &&
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
            ) {
                errors.email = 'Please enter a valid email address';
            }
            if (formData.website && hasUnsupportedScheme(formData.website)) {
                errors.website = 'Website must start with http:// or https://';
            }
            if (!formData.timezone) {
                errors.timezone = 'Timezone is required';
            }
        }

        // Step 3 has no required fields

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Final form submission handler (triggered on step 4).
    // Two-phase process:
    // 1. If a logo file was selected, upload it to S3 first and get the URL
    // 2. Build the submission payload and POST to create the organization
    // The offerings and needs fields are converted from newline-delimited text
    // to arrays. On success, a brief message is shown before redirecting to dashboard.
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            let imageUrl = formData.imageUrl;

            // Phase 1: Upload logo to S3 if a file was selected
            if (formData.imageFile) {
                setUploadingImage(true);
                try {
                    const uploadResult = await orgApi.uploadLogo(
                        formData.imageFile
                    );
                    imageUrl = uploadResult.imageUrl;
                } catch (uploadErr) {
                    setError(
                        uploadErr.response?.data?.message ||
                            'Failed to upload logo. Please try again.'
                    );
                    setLoading(false);
                    setUploadingImage(false);
                    return;
                }
                setUploadingImage(false);
            }

            // Phase 2: Build and submit the organization payload.
            // 'type' is a legacy field required by the server schema alongside primarySector.
            const submitData = {
                name: formData.name.trim(),
                type: formData.primarySector, // Required by server
                primarySector: formData.primarySector,
                secondarySectors: formData.secondarySectors,
                description: formData.description.trim(),
                email: formData.email?.trim() || undefined,
                website: normalizeUrl(formData.website?.trim()) || undefined,
                imageUrl: imageUrl || undefined,
                location: {
                    address: formData.address?.trim() || undefined,
                    city: formData.city?.trim() || undefined,
                    state: formData.state?.trim() || undefined,
                    zipCode: formData.zipCode?.trim() || undefined,
                    timezone: formData.timezone,
                },
                regionsServed: formData.regionsServed,
                programmingTypes: formData.programmingTypes,
                offerings: formData.offerings
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                needs: formData.needs
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
            };

            await orgApi.createOrganization(submitData);
            setSuccess(
                'Organization submitted successfully! It will be reviewed by our team.'
            );
            setTimeout(() => {
                // Hand off to the ecosystem chooser. After creating an
                // organization the user is implicitly a member of the
                // ecosystem the org was created in, so the 1-membership case
                // is the common one and the chooser auto-routes to that
                // tenant's dashboard. The N case handles the rare power-user
                // who already belonged to multiple ecosystems.
                navigate('/choose-ecosystem');
            }, 2000);
        } catch (err) {
            setError(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    'Failed to submit organization. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    // ---- Wizard Navigation Handlers ----

    // Advance to next step only if current step passes validation
    const nextStep = () => {
        if (validateStep(step)) {
            setStep((s) => Math.min(s + 1, 4));
            setError('');
        }
    };

    const prevStep = () => setStep((s) => Math.max(s - 1, 1));

    // Allow clicking step indicators to jump backward to a previous step.
    // Forward navigation is blocked (stepNum <= step) to enforce sequential completion.
    const goToStep = (stepNum) => {
        if (stepNum <= step) {
            setStep(stepNum);
        }
    };

    // Show loading while checking auth
    if (authLoading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Register Organization | RILE Connect</title>
            </Helmet>
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <h1 className="text-primary mb-4">
                            Register Your Organization
                        </h1>
                        <p className="lead mb-4">
                            Register your organization on RILE Connect to
                            connect with {ecosystemName}.
                        </p>

                        {/* Step indicators: visual breadcrumb showing progress.
                            Completed steps show a green checkmark, current step is blue,
                            future steps are grey and non-interactive. Clicking a completed
                            step navigates back to it via goToStep. */}
                        <div className="d-flex justify-content-between mb-4">
                            {STEPS.map((s) => (
                                <div
                                    key={s.num}
                                    className={`text-center flex-fill ${
                                        s.num <= step
                                            ? 'text-primary step-clickable'
                                            : 'text-muted'
                                    }`}
                                    onClick={() => goToStep(s.num)}
                                    role="button"
                                    tabIndex={s.num <= step ? 0 : -1}
                                    onKeyDown={(e) =>
                                        e.key === 'Enter' && goToStep(s.num)
                                    }
                                >
                                    <div
                                        className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-2 step-circle ${
                                            s.num < step
                                                ? 'bg-success text-white'
                                                : s.num === step
                                                  ? 'bg-primary text-white'
                                                  : 'bg-light text-muted'
                                        }`}
                                    >
                                        {s.num < step ? '✓' : s.icon}
                                    </div>
                                    <div className="small">{s.title}</div>
                                </div>
                            ))}
                        </div>

                        <ProgressBar
                            now={(step / 4) * 100}
                            className="mb-4"
                            variant="primary"
                        />

                        {error && <Alert variant="danger">{error}</Alert>}
                        {success && <Alert variant="success">{success}</Alert>}

                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <Form onSubmit={handleSubmit}>
                                    {/* Step 1: Basic Info */}
                                    {step === 1 && (
                                        <>
                                            <h4 className="text-primary mb-4">
                                                Basic Information
                                            </h4>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Organization Name *
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={(e) => {
                                                        handleChange(e);
                                                        checkNameAvailability(
                                                            e.target.value
                                                        );
                                                    }}
                                                    isInvalid={
                                                        !!validationErrors.name
                                                    }
                                                    placeholder="Enter your organization's name"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {validationErrors.name}
                                                </Form.Control.Feedback>
                                                {nameCheckLoading && (
                                                    <Form.Text className="text-muted">
                                                        <Spinner
                                                            size="sm"
                                                            animation="border"
                                                            className="me-1"
                                                        />
                                                        Checking name
                                                        availability...
                                                    </Form.Text>
                                                )}
                                                {nameExists &&
                                                    !validationErrors.name && (
                                                        <Form.Text className="text-warning">
                                                            An organization with
                                                            this name already
                                                            exists. You may want
                                                            to claim it from the
                                                            directory instead.
                                                        </Form.Text>
                                                    )}
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Primary Sector *
                                                </Form.Label>
                                                <Form.Select
                                                    name="primarySector"
                                                    value={
                                                        formData.primarySector
                                                    }
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        !!validationErrors.primarySector
                                                    }
                                                >
                                                    <option value="">
                                                        Select a sector
                                                    </option>
                                                    {sectors.map((s) => (
                                                        <option
                                                            key={s}
                                                            value={s}
                                                        >
                                                            {s}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">
                                                    {
                                                        validationErrors.primarySector
                                                    }
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Secondary Sectors
                                                </Form.Label>
                                                <Form.Text className="d-block mb-2 text-muted">
                                                    Select any additional
                                                    sectors that apply
                                                </Form.Text>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {sectors.map((s) => (
                                                        <Form.Check
                                                            key={s}
                                                            type="checkbox"
                                                            id={`sector-${s}`}
                                                            label={s}
                                                            checked={formData.secondarySectors.includes(
                                                                s
                                                            )}
                                                            onChange={() =>
                                                                handleMultiSelect(
                                                                    'secondarySectors',
                                                                    s
                                                                )
                                                            }
                                                            disabled={
                                                                s ===
                                                                formData.primarySector
                                                            }
                                                        />
                                                    ))}
                                                </div>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Description *
                                                </Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={4}
                                                    name="description"
                                                    value={formData.description}
                                                    onChange={handleChange}
                                                    placeholder="Tell us about your organization and its mission..."
                                                    isInvalid={
                                                        !!validationErrors.description
                                                    }
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {
                                                        validationErrors.description
                                                    }
                                                </Form.Control.Feedback>
                                                <Form.Text className="text-muted">
                                                    {
                                                        formData.description
                                                            .length
                                                    }
                                                    /2000 characters
                                                </Form.Text>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Organization Logo
                                                </Form.Label>
                                                <Form.Text className="d-block mb-2 text-muted">
                                                    Upload your
                                                    organization&apos;s logo
                                                    (optional, max 5MB)
                                                </Form.Text>
                                                <div className="d-flex align-items-center gap-3">
                                                    {imagePreview && (
                                                        <img
                                                            src={imagePreview}
                                                            alt="Preview"
                                                            className="logo-preview-md"
                                                        />
                                                    )}
                                                    <div>
                                                        <input
                                                            type="file"
                                                            ref={fileInputRef}
                                                            onChange={
                                                                handleImageChange
                                                            }
                                                            accept="image/*"
                                                            className="d-none"
                                                        />
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                fileInputRef.current?.click()
                                                            }
                                                            disabled={
                                                                uploadingImage
                                                            }
                                                        >
                                                            {imagePreview
                                                                ? 'Change Image'
                                                                : 'Choose Image'}
                                                        </Button>
                                                        {imagePreview && (
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="text-danger"
                                                                onClick={() => {
                                                                    setImagePreview(
                                                                        null
                                                                    );
                                                                    setFormData(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            imageFile:
                                                                                null,
                                                                            imageUrl:
                                                                                '',
                                                                        })
                                                                    );
                                                                }}
                                                            >
                                                                Remove
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </Form.Group>
                                        </>
                                    )}

                                    {/* Step 2: Contact & Location */}
                                    {step === 2 && (
                                        <>
                                            <h4 className="text-primary mb-4">
                                                Contact & Location
                                            </h4>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Contact Email
                                                </Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder="contact@organization.org"
                                                    isInvalid={
                                                        !!validationErrors.email
                                                    }
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {validationErrors.email}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>Website</Form.Label>
                                                <Form.Control
                                                    type="url"
                                                    name="website"
                                                    value={formData.website}
                                                    onChange={handleChange}
                                                    placeholder="https://www.organization.org"
                                                    isInvalid={
                                                        !!validationErrors.website
                                                    }
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {validationErrors.website}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Street Address
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>

                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            City
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="city"
                                                            value={
                                                                formData.city
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            State
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="state"
                                                            value={
                                                                formData.state
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            ZIP Code
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="zipCode"
                                                            value={
                                                                formData.zipCode
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Timezone *
                                                </Form.Label>
                                                <Form.Select
                                                    name="timezone"
                                                    value={formData.timezone}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        !!validationErrors.timezone
                                                    }
                                                >
                                                    <option value="">
                                                        Select a timezone
                                                    </option>
                                                    {US_TIMEZONES.map((tz) => (
                                                        <option
                                                            key={tz.value}
                                                            value={tz.value}
                                                        >
                                                            {tz.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">
                                                    {validationErrors.timezone}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Regions Served *
                                                </Form.Label>
                                                <Form.Text className="d-block mb-2 text-muted">
                                                    Select all regions your
                                                    organization serves
                                                </Form.Text>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {regions.map((r) => (
                                                        <Form.Check
                                                            key={r}
                                                            type="checkbox"
                                                            id={`region-${r}`}
                                                            label={r}
                                                            checked={formData.regionsServed.includes(
                                                                r
                                                            )}
                                                            onChange={() =>
                                                                handleMultiSelect(
                                                                    'regionsServed',
                                                                    r
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </div>
                                                {validationErrors.regionsServed && (
                                                    <div className="text-danger small mt-1">
                                                        {
                                                            validationErrors.regionsServed
                                                        }
                                                    </div>
                                                )}
                                            </Form.Group>
                                        </>
                                    )}

                                    {/* Step 3: Programs & Needs */}
                                    {step === 3 && (
                                        <>
                                            <h4 className="text-primary mb-4">
                                                Programs & Partnerships
                                            </h4>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Programming Types
                                                </Form.Label>
                                                <Form.Text className="d-block mb-2 text-muted">
                                                    What types of programming
                                                    does your organization
                                                    offer?
                                                </Form.Text>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {taxonomyProgrammingTypes.map(
                                                        (p) => (
                                                            <Form.Check
                                                                key={p}
                                                                type="checkbox"
                                                                id={`program-${p}`}
                                                                label={p}
                                                                checked={formData.programmingTypes.includes(
                                                                    p
                                                                )}
                                                                onChange={() =>
                                                                    handleMultiSelect(
                                                                        'programmingTypes',
                                                                        p
                                                                    )
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </div>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    What We Offer
                                                </Form.Label>
                                                <Form.Text className="d-block mb-2 text-muted">
                                                    What resources or services
                                                    can your organization
                                                    provide to partners? (one
                                                    per line)
                                                </Form.Text>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    name="offerings"
                                                    value={formData.offerings}
                                                    onChange={handleChange}
                                                    placeholder="Professional development&#10;Curriculum resources&#10;Industry connections"
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    What We Need
                                                </Form.Label>
                                                <Form.Text className="d-block mb-2 text-muted">
                                                    What resources or support is
                                                    your organization looking
                                                    for? (one per line)
                                                </Form.Text>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    name="needs"
                                                    value={formData.needs}
                                                    onChange={handleChange}
                                                    placeholder="Volunteers&#10;Funding&#10;Partnerships"
                                                />
                                            </Form.Group>
                                        </>
                                    )}

                                    {/* Step 4: Review */}
                                    {step === 4 && (
                                        <>
                                            <h4 className="text-primary mb-4">
                                                Review Your Submission
                                            </h4>
                                            <p className="text-muted mb-4">
                                                Please review your information
                                                before submitting. You can click
                                                on any section to go back and
                                                make changes.
                                            </p>

                                            {/* Basic Info Review */}
                                            <Card className="mb-3 border">
                                                <Card.Header
                                                    className="bg-light d-flex justify-content-between align-items-center step-clickable"
                                                    role="button"
                                                    onClick={() => setStep(1)}
                                                >
                                                    <strong>
                                                        Basic Information
                                                    </strong>
                                                    <small className="text-primary">
                                                        Edit
                                                    </small>
                                                </Card.Header>
                                                <Card.Body>
                                                    <Row>
                                                        <Col
                                                            md={
                                                                imagePreview
                                                                    ? 9
                                                                    : 12
                                                            }
                                                        >
                                                            <p className="mb-2">
                                                                <strong>
                                                                    Name:
                                                                </strong>{' '}
                                                                {formData.name}
                                                            </p>
                                                            <p className="mb-2">
                                                                <strong>
                                                                    Primary
                                                                    Sector:
                                                                </strong>{' '}
                                                                <Badge bg="primary">
                                                                    {
                                                                        formData.primarySector
                                                                    }
                                                                </Badge>
                                                            </p>
                                                            {formData
                                                                .secondarySectors
                                                                .length > 0 && (
                                                                <p className="mb-2">
                                                                    <strong>
                                                                        Secondary
                                                                        Sectors:
                                                                    </strong>{' '}
                                                                    {formData.secondarySectors.map(
                                                                        (s) => (
                                                                            <Badge
                                                                                bg="secondary"
                                                                                key={
                                                                                    s
                                                                                }
                                                                                className="me-1"
                                                                            >
                                                                                {
                                                                                    s
                                                                                }
                                                                            </Badge>
                                                                        )
                                                                    )}
                                                                </p>
                                                            )}
                                                            <p className="mb-0">
                                                                <strong>
                                                                    Description:
                                                                </strong>
                                                                <br />
                                                                {
                                                                    formData.description
                                                                }
                                                            </p>
                                                        </Col>
                                                        {imagePreview && (
                                                            <Col
                                                                md={3}
                                                                className="text-end"
                                                            >
                                                                <img
                                                                    src={
                                                                        imagePreview
                                                                    }
                                                                    alt="Logo preview"
                                                                    className="logo-preview-sm"
                                                                />
                                                            </Col>
                                                        )}
                                                    </Row>
                                                </Card.Body>
                                            </Card>

                                            {/* Contact & Location Review */}
                                            <Card className="mb-3 border">
                                                <Card.Header
                                                    className="bg-light d-flex justify-content-between align-items-center step-clickable"
                                                    role="button"
                                                    onClick={() => setStep(2)}
                                                >
                                                    <strong>
                                                        Contact & Location
                                                    </strong>
                                                    <small className="text-primary">
                                                        Edit
                                                    </small>
                                                </Card.Header>
                                                <Card.Body>
                                                    {formData.email && (
                                                        <p className="mb-2">
                                                            <strong>
                                                                Email:
                                                            </strong>{' '}
                                                            {formData.email}
                                                        </p>
                                                    )}
                                                    {formData.website && (
                                                        <p className="mb-2">
                                                            <strong>
                                                                Website:
                                                            </strong>{' '}
                                                            {formData.website}
                                                        </p>
                                                    )}
                                                    <p className="mb-2">
                                                        <strong>
                                                            Timezone:
                                                        </strong>{' '}
                                                        {formData.timezone}
                                                    </p>
                                                    <p className="mb-0">
                                                        <strong>
                                                            Regions Served:
                                                        </strong>{' '}
                                                        {formData.regionsServed.map(
                                                            (r) => (
                                                                <Badge
                                                                    bg="info"
                                                                    key={r}
                                                                    className="me-1"
                                                                >
                                                                    {r}
                                                                </Badge>
                                                            )
                                                        )}
                                                    </p>
                                                </Card.Body>
                                            </Card>

                                            {/* Programs Review */}
                                            <Card className="mb-3 border">
                                                <Card.Header
                                                    className="bg-light d-flex justify-content-between align-items-center step-clickable"
                                                    role="button"
                                                    onClick={() => setStep(3)}
                                                >
                                                    <strong>
                                                        Programs & Partnerships
                                                    </strong>
                                                    <small className="text-primary">
                                                        Edit
                                                    </small>
                                                </Card.Header>
                                                <Card.Body>
                                                    {formData.programmingTypes
                                                        .length > 0 && (
                                                        <p className="mb-2">
                                                            <strong>
                                                                Programming
                                                                Types:
                                                            </strong>{' '}
                                                            {formData.programmingTypes.map(
                                                                (p) => (
                                                                    <Badge
                                                                        bg="success"
                                                                        key={p}
                                                                        className="me-1 mb-1"
                                                                    >
                                                                        {p}
                                                                    </Badge>
                                                                )
                                                            )}
                                                        </p>
                                                    )}
                                                    {formData.offerings.trim() && (
                                                        <div className="mb-2">
                                                            <strong>
                                                                What We Offer:
                                                            </strong>
                                                            <ul className="mb-0 mt-1">
                                                                {formData.offerings
                                                                    .split('\n')
                                                                    .filter(
                                                                        Boolean
                                                                    )
                                                                    .map(
                                                                        (
                                                                            item,
                                                                            i
                                                                        ) => (
                                                                            <li
                                                                                key={
                                                                                    i
                                                                                }
                                                                            >
                                                                                {
                                                                                    item
                                                                                }
                                                                            </li>
                                                                        )
                                                                    )}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {formData.needs.trim() && (
                                                        <div className="mb-0">
                                                            <strong>
                                                                What We Need:
                                                            </strong>
                                                            <ul className="mb-0 mt-1">
                                                                {formData.needs
                                                                    .split('\n')
                                                                    .filter(
                                                                        Boolean
                                                                    )
                                                                    .map(
                                                                        (
                                                                            item,
                                                                            i
                                                                        ) => (
                                                                            <li
                                                                                key={
                                                                                    i
                                                                                }
                                                                            >
                                                                                {
                                                                                    item
                                                                                }
                                                                            </li>
                                                                        )
                                                                    )}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {!formData.programmingTypes
                                                        .length &&
                                                        !formData.offerings.trim() &&
                                                        !formData.needs.trim() && (
                                                            <p className="text-muted mb-0">
                                                                No programs or
                                                                partnership info
                                                                provided
                                                            </p>
                                                        )}
                                                </Card.Body>
                                            </Card>

                                            <Alert
                                                variant="info"
                                                className="mt-4"
                                            >
                                                <strong>
                                                    What happens next?
                                                </strong>
                                                <br />
                                                Your organization will be
                                                reviewed by our team. Once
                                                approved, it will appear in the
                                                ecosystem directory and
                                                you&apos;ll be able to connect
                                                with other organizations.
                                            </Alert>
                                        </>
                                    )}

                                    {/* Navigation: Previous/Next or Submit buttons.
                                        Step 1 hides "Previous" (empty div for flex spacing).
                                        Steps 1-3 show "Next" (triggers validation before advancing).
                                        Step 4 shows "Submit Organization" (triggers handleSubmit).
                                        The submit button shows different text during image upload
                                        vs. API submission to give the user clear feedback. */}
                                    <div className="d-flex justify-content-between mt-4">
                                        {step > 1 ? (
                                            <Button
                                                variant="outline-secondary"
                                                onClick={prevStep}
                                                disabled={loading}
                                            >
                                                Previous
                                            </Button>
                                        ) : (
                                            <div />
                                        )}

                                        {step < 4 ? (
                                            <Button
                                                variant="primary"
                                                onClick={nextStep}
                                            >
                                                Next
                                            </Button>
                                        ) : (
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                disabled={
                                                    loading || uploadingImage
                                                }
                                            >
                                                {uploadingImage ? (
                                                    <>
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            className="me-2"
                                                        />
                                                        Uploading Logo...
                                                    </>
                                                ) : loading ? (
                                                    <>
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            className="me-2"
                                                        />
                                                        Submitting...
                                                    </>
                                                ) : (
                                                    'Submit Organization'
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
